import { toast } from 'react-toastify'
import { getErrorMessage } from '../../helpers'
import { BaseApiResponse } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  CreateHomePageSection,
  CreateSectionResponse,
  GetHomePageResponse,
} from './homePage.type'

export const goodHomeApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    createHomeSection: builder.mutation<
      CreateSectionResponse,
      CreateHomePageSection
    >({
      query: (payload) => ({
        url: `v2/good-homepage/sections`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'HOMEPAGE_SECTION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const res = await queryFulfilled
          if (res.data.success) {
            toast.success(res.data.message)
          }
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getHomePageSection: builder.query<CreateSectionResponse, { id: string }>({
      query: ({ id }) => ({
        url: `v2/good-homepage/sections/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.id },
      ],
      transformResponse: (data: CreateSectionResponse) => {
        return {
          ...data,
          data: {
            ...data.data,
            items: data.data.items.map((it) => {
              return {
                ...it,
                manualContent: it.manualContent
                  ? {
                      ...it.manualContent,
                      _id: `${it.manualContent.title}`,
                    }
                  : undefined,
              }
            }),
          },
        }
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(error as string)
        }
      },
    }),
    updateHomeSection: builder.mutation<
      CreateSectionResponse,
      { id: string; payload: CreateHomePageSection }
    >({
      query: ({ payload, id }) => ({
        url: `v2/good-homepage/sections/${id}`,
        method: 'PUT',
        body: { ...payload },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.id },
        { type: 'HOMEPAGE_SECTION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteHomeSection: builder.mutation<BaseApiResponse, { id: string }>({
      query: ({ id }) => ({
        url: `v2/good-homepage/sections/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'HOMEPAGE_SECTION', id: arg.id },
        { type: 'HOMEPAGE_SECTION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    reorderHomeSection: builder.mutation<
      BaseApiResponse,
      { sectionIds: string[] }
    >({
      query: (payload) => ({
        url: `v2/good-homepage/sections/reorder`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'HOMEPAGE_SECTION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getHomePage: builder.query<GetHomePageResponse, void>({
      query: () => ({
        url: `v2/good-homepage`,
        method: 'GET',
      }),
      providesTags: [{ type: 'HOMEPAGE_SECTION', id: 'LIST' }],
      transformResponse: (data: GetHomePageResponse) => {
        return {
          ...data,
          data: {
            ...data.data,
            sections: data.data.sections.map((section) => {
              return {
                ...section,
                items: section.items.map((it) => {
                  return {
                    ...it,
                    manualContent: it.manualContent
                      ? {
                          ...it.manualContent,
                          _id: `${it.manualContent.title}`,
                        }
                      : undefined,
                  }
                }),
              }
            }),
          },
        }
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(error as string)
        }
      },
    }),
  }),
})

export const {
  useCreateHomeSectionMutation,
  useGetHomePageQuery,
  useGetHomePageSectionQuery,
  useReorderHomeSectionMutation,
  useUpdateHomeSectionMutation,
  useDeleteHomeSectionMutation,
} = goodHomeApiSlice
